import { Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Search } from "@mui/icons-material";
import { BORDER, METRIC_CONFIG, METRIC_TITLES, ROWS_PER_PAGE } from "./constants";
import MetricsBar from "./MetricsBar";
import ResultsTable from "./ResultsTable";
import PaginationFooter from "./PaginationFooter";

const ProductionDetailModal = ({
  open,
  onClose,
  metricType,
  data,
  loading,
  error,
  metricCounts = {},
  onMetricChange,
  showMetricsBar = true,
}) => {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    if (open) {
      setSearchText("");
      setPage(0);
      setSortField(null);
      setSortDir("asc");
    }
  }, [open, metricType]);

  const title = METRIC_TITLES[metricType] || "Détails";
  const config = METRIC_CONFIG[metricType] || METRIC_CONFIG.Envoi;
  const { Icon } = config;
  const totalCount = (data ?? []).length;

  const filtered = useMemo(() => {
    const rows = data ?? [];
    if (!searchText) return rows;
    const q = searchText.toLowerCase();
    return rows.filter(
      (row) =>
        (row.candidatNom || "").toString().toLowerCase().includes(q) ||
        (row.candidatPrenom || "").toString().toLowerCase().includes(q) ||
        (row.missionName || "").toString().toLowerCase().includes(q) ||
        (row.societeName || "").toString().toLowerCase().includes(q),
    );
  }, [data, searchText]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = (a[sortField] || "").toString().toLowerCase();
      const vb = (b[sortField] || "").toString().toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortField, sortDir]);

  const totalRows = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const startIdx = safePage * ROWS_PER_PAGE;
  const endIdx = Math.min(startIdx + ROWS_PER_PAGE, totalRows);
  const pageRows = sorted.slice(startIdx, endIdx);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) setPage(newPage);
  };

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const set = new Set([0, totalPages - 1, safePage]);
    for (
      let i = Math.max(0, safePage - 1);
      i <= Math.min(totalPages - 1, safePage + 1);
      i++
    )
      set.add(i);
    return [...set].sort((a, b) => a - b);
  }, [totalPages, safePage]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ borderBottom: BORDER, py: 1.5, px: 2.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: config.bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 18, color: config.color }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}
              >
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalCount} résultat{totalCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.25 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading && (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box p={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && (
          <>
            {showMetricsBar && (
              <MetricsBar
                metricType={metricType}
                metricCounts={metricCounts}
                onMetricChange={onMetricChange}
              />
            )}

            <Box sx={{ px: 2.5, py: 1.25, borderBottom: BORDER }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher par candidat, mission, client…"
                value={searchText}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 17, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: 13,
                  },
                }}
              />
            </Box>

            <ResultsTable
              pageRows={pageRows}
              startIdx={startIdx}
              sortField={sortField}
              sortDir={sortDir}
              onSort={handleSort}
            />

            <PaginationFooter
              totalRows={totalRows}
              startIdx={startIdx}
              endIdx={endIdx}
              safePage={safePage}
              totalPages={totalPages}
              pageNumbers={pageNumbers}
              onPageChange={handlePageChange}
              showBorder={showMetricsBar}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductionDetailModal;
